<script setup lang="ts">
// Support request. Reached from the "Need help?" affordance, which matters more
// here than at a booth: the person holding the phone has no account, no
// relationship with us, and no other obvious way to reach anyone.
const emit = defineEmits<{ close: [] }>()

const config = useRuntimeConfig()
const device = useScanDevice()
const online = useOnline()
const { send } = useSupportRequest()

const message = ref('')
const sending = ref(false)

useModalBehaviour(() => emit('close'))

// Three outcomes rather than two: sent, kept on the phone for later, or nothing
// chosen yet. The middle one is the case the fallback number exists for.
const result = ref<'sent' | 'queued' | null>(null)

// Shown as plain text, never as a tel: link. The fleet phones are Wi-Fi only, carry
// no SIM and run locked to this app, so a tap on a link would do nothing and read
// as broken. The number is for the phone in the reader's own pocket, and the copy
// around it has to say so, or "call us" looks like an instruction for this device.
const supportPhone = computed(() => String(config.public.supportPhone || ''))

const confirm = async () => {
  if (sending.value) {
    return
  }

  sending.value = true
  result.value = await send(message.value.trim()) ? 'sent' : 'queued'
  sending.value = false
}
</script>

<template>
  <!-- Tapping outside the panel closes it, the same as the cancel button. -->
  <div
    class="support"
    role="dialog"
    aria-modal="true"
    aria-label="Need help"
    @click.self="emit('close')"
  >
    <div class="support__panel">
      <template v-if="!result">
        <h2 class="support__title">Need help?</h2>

        <!-- Said before anything is typed, because it changes what the person should
             do: a message raised now waits on the phone, and the number below is the
             way to reach somebody in the meantime. -->
        <p v-if="!online" class="support__offline" role="status" aria-live="polite">
          This phone has no connection right now, so a message will not reach us until
          it reconnects.
        </p>

        <textarea
          v-model="message"
          class="support__message"
          rows="3"
          maxlength="500"
          placeholder="What is wrong? (optional)"
          aria-label="Optional message"
        />

        <!-- Says plainly that this is about to reach a person, which is what stops
             the button being pressed to see what it does. -->
        <p class="support__confirm-text">
          This sends a message to the WeAreDevelopers team with this phone's name
          and status. Send it?
        </p>

        <button
          type="button"
          class="wad4-button wad4-button-primary wad4-button-full"
          :disabled="sending"
          @click="confirm"
        >
          {{ sending ? 'Sending' : 'Yes, send it' }}
        </button>

        <div v-if="supportPhone" class="support__call">
          <p class="support__call-text">
            This phone is Wi-Fi only and cannot make calls. If it is offline, or you need
            someone now, call us from your own phone:
          </p>
          <strong class="support__phone">{{ supportPhone }}</strong>
        </div>

        <button type="button" class="support__cancel" @click="emit('close')">
          Cancel
        </button>
      </template>

      <template v-else-if="result === 'sent'">
        <h2 class="support__title">Help is on the way</h2>
        <p class="support__hint">
          We have been told about {{ device.deviceLabel }}.
        </p>

        <button
          type="button"
          class="wad4-button wad4-button-primary wad4-button-full"
          @click="emit('close')"
        >
          Close
        </button>
      </template>

      <template v-else>
        <!-- Not phrased as a connection problem, because it also covers our end
             being unable to pass the message on. Either way nobody has seen it yet,
             and the number below is the way through in the meantime. -->
        <h2 class="support__title">Not sent yet</h2>
        <p class="support__hint">
          Your message is saved on this phone and will be sent as soon as it gets
          through.
        </p>

        <div class="support__call">
          <p class="support__call-text">
            <template v-if="supportPhone">
              This phone is Wi-Fi only and cannot make calls. For help now, find
              WeAreDevelopers staff at the info desk, or call us from your own phone:
            </template>
            <template v-else>
              For help now, find WeAreDevelopers staff at the info desk.
            </template>
          </p>
          <strong v-if="supportPhone" class="support__phone">{{ supportPhone }}</strong>
        </div>

        <button
          type="button"
          class="wad4-button wad4-button-primary wad4-button-full"
          @click="emit('close')"
        >
          Close
        </button>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.support {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  // Keeps the panel clear of the screen edges and of the notch.
  padding: max(#{$wad4-spacing-6}, env(safe-area-inset-top)) $wad4-spacing-4
    max(#{$wad4-spacing-6}, env(safe-area-inset-bottom));
  background-color: rgba(0, 0, 0, 0.6);

  &__panel {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-3;
    width: 100%;
    max-width: 480px;
    max-height: 100%;
    overflow-y: auto;
    padding: $wad4-spacing-6 $wad4-spacing-4;
    background-color: $wad4-c-background-bg-primary;
    border-radius: $wad4-border-radius-lg;
  }

  &__title {
    margin: 0;
    @include title-display-mixin('xs');
  }

  &__hint {
    margin: 0;
    color: $wad4-c-text-text-secondary;
  }

  &__offline {
    margin: 0;
    @include text-size-mixin('sm');
    color: $wad4-c-text-text-secondary;
  }

  &__message {
    padding: $wad4-spacing-3;
    font-family: $wad4-default-font-sans;
    @include text-size-mixin('md');
    color: $wad4-c-text-text-primary;
    background-color: $wad4-c-background-bg-secondary;
    border: 1px solid $wad4-c-border-border-primary;
    border-radius: $wad4-border-radius-md;
    resize: none;
    // Text selection is off across the app, and typing fields are the exception.
    -webkit-user-select: text;
    user-select: text;
  }

  &__confirm-text {
    margin: 0;
    @include text-size-mixin('sm');
    color: $wad4-c-text-text-secondary;
  }

  // The same block in both states where nobody has been reached yet, so the number
  // is found in the same place whichever screen somebody is looking at.
  &__call {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-2;
    padding: $wad4-spacing-3;
    text-align: center;
    background-color: $wad4-c-background-bg-secondary;
    border-radius: $wad4-border-radius-md;
  }

  &__call-text {
    margin: 0;
    @include text-size-mixin('sm');
    color: $wad4-c-text-text-secondary;
  }

  &__phone {
    @include text-size-mixin('lg');
    font-weight: $wad4-font-weight-semi-bold;
    color: $wad4-c-text-text-brand-tertiery;
    // Read off this screen and typed into another phone, so it must never wrap.
    white-space: nowrap;
  }

  &__cancel {
    padding: $wad4-spacing-3;
    font-family: $wad4-default-font-sans;
    @include text-size-mixin('sm');
    color: $wad4-c-text-text-secondary;
    background: none;
    border: none;
    cursor: pointer;
  }
}
</style>
